import { supabase } from "@/integrations/supabase/client";
import type { ExtractedDocument, Appointment, Patient } from "@/integrations/supabase/types.docread";

/**
 * Atualiza extracted_documents com o resultado da análise de IA
 */
export async function updateExtractedAnalysisByPath(
  patientId: string,
  fileName: string,
  filePath: string,
  payload: { lab_results: string; summary: string; insights: string[]; risk_level: string }
) {
  console.log('[updateExtractedAnalysis] Buscando documento:', {
    patientId,
    fileName,
    filePath
  });

  const { data, error } = await supabase
    .from("documents_onboarding")
    .update({
      extracted_text: payload.lab_results,
      ai_summary: payload.summary,
      ai_insights: payload.insights,
      ai_risk_level: payload.risk_level,
      status: 'analyzed'
    })
    .eq("patient_id", patientId)
    .eq("file_name", fileName)
    .eq("file_path", filePath)
    .select();

  if (error) throw new Error(`DB update documents_onboarding: ${error.message}`);

  if (!data || data.length === 0) {
    console.error('[updateExtractedAnalysis] Nenhum registro encontrado para atualizar');
    throw new Error(`Documento não encontrado no banco: ${fileName}`);
  }

  console.log('[updateExtractedAnalysis] Sucesso - registros atualizados:', data.length);
}

/**
 * Lista appointments de um paciente
 */
export async function listAppointmentsByPatient(patientId: string): Promise<Appointment[]> {
  const { data, error } = await supabase
    .from("appointments")
    .select("*")
    .eq("patient_id", patientId)
    .order("appointment_date", { ascending: false });

  if (error) throw new Error(error.message);
  return (data || []) as Appointment[];
}

/**
 * Lista resultados (extracted_documents) de um paciente
 */
export async function listResultsByPatient(patientId: string): Promise<any[]> {
  const { data, error } = await supabase
    .from("documents_onboarding")
    .select("*")
    .eq("patient_id", patientId)
    .order("created_at", { ascending: false });

  if (error) throw new Error(error.message);
  return data || [];
}

/**
 * Busca um paciente por ID
 */
export async function getPatient(patientId: string): Promise<Patient | null> {
  const { data, error } = await supabase
    .from("patients_onboarding")
    .select("*")
    .eq("id", patientId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw new Error(error.message);
  }
  return data as Patient;
}

/**
 * Cria ou retorna paciente existente por CPF
 */
export async function ensurePatientByCPF(input: { name: string; cpf?: string }): Promise<Patient> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Usuário não autenticado");

  const cpfDigits = input.cpf ? input.cpf.replace(/\D/g, "") : "";

  // If has CPF, try to find existing (CPF armazenado como 11 dígitos)
  if (cpfDigits.length === 11) {
    const { data: existing } = await supabase
      .from("patients_onboarding")
      .select("*")
      .eq("owner_id", user.id)
      .eq("cpf", cpfDigits)
      .maybeSingle();

    if (existing) return existing as Patient;
  }

  // Validar limite de pacientes do plano antes de criar
  const subscriptionStore = (await import("@/stores/subscriptionStore")).useSubscriptionStore;
  const { canAddPatient, currentPlan, patientsCount } = subscriptionStore.getState();
  if (!canAddPatient()) {
    const maxPatients = currentPlan?.max_patients || 10;
    throw new Error(`Limite de pacientes atingido. Você possui ${patientsCount} pacientes ativos e seu plano permite no máximo ${maxPatients}. Faça upgrade do seu plano para adicionar mais pacientes.`);
  }

  // Create new
  const { data, error } = await supabase
    .from("patients_onboarding")
    .insert({
      owner_id: user.id,
      full_name: input.name,
      cpf: cpfDigits.length === 11 ? cpfDigits : input.cpf || null,
    })
    .select()
    .single();

  if (error) throw new Error(`Erro ao criar paciente: ${error.message}`);
  
  // Atualizar contagem de pacientes no store
  subscriptionStore.getState().refreshPatientsCount();
  
  return data as Patient;
}

/**
 * Cria um novo appointment
 */
export async function createAppointment(patientId: string, notes?: string): Promise<Appointment> {
  const { data, error } = await supabase
    .from("appointments")
    .insert({
      patient_id: patientId,
      appointment_date: new Date().toISOString(),
      notes: notes || null,
    })
    .select()
    .single();

  if (error) throw new Error(`Erro ao criar atendimento: ${error.message}`);
  return data as Appointment;
}

/**
 * Helper para fazer parse de intervalos de referência
 */
function parseReferenceRange(refString: string): { ref_low: number | null; ref_high: number | null } {
  if (!refString) return { ref_low: null, ref_high: null };

  const clean = refString.trim();

  // Pattern: "30-400" ou "30 a 400"
  const rangeMatch = clean.match(/(\d+(?:\.\d+)?)\s*(?:a|-|até)\s*(\d+(?:\.\d+)?)/i);
  if (rangeMatch) {
    return {
      ref_low: parseFloat(rangeMatch[1]),
      ref_high: parseFloat(rangeMatch[2])
    };
  }

  // Pattern: "< 150"
  const lessThanMatch = clean.match(/[<≤]\s*(\d+(?:\.\d+)?)/);
  if (lessThanMatch) {
    return {
      ref_low: null,
      ref_high: parseFloat(lessThanMatch[1])
    };
  }

  // Pattern: "> 10"
  const greaterThanMatch = clean.match(/[>≥]\s*(\d+(?:\.\d+)?)/);
  if (greaterThanMatch) {
    return {
      ref_low: parseFloat(greaterThanMatch[1]),
      ref_high: null
    };
  }

  return { ref_low: null, ref_high: null };
}

/**
 * Extrai e grava resultados laboratoriais estruturados em lab_results_onboarding
 */
export async function saveLabResultsFromAnalysis(
  patientId: string,
  documentId: string,
  analysisData: any
) {
  if (!analysisData?.structured?.tests || !Array.isArray(analysisData.structured.tests)) {
    console.warn('[saveLabResults] Nenhum teste estruturado encontrado');
    return { inserted: 0, errors: 0 };
  }

  const collectedDate = analysisData.document_date ||
    analysisData.structured?.date_inferred ||
    new Date().toISOString().split('T')[0];

  console.log('[saveLabResults] Processando testes:', {
    patientId,
    documentId,
    testsCount: analysisData.structured.tests.length,
    collectedDate
  });

  let inserted = 0;
  let errors = 0;

  for (const test of analysisData.structured.tests) {
    try {
      // Parse reference range
      const { ref_low, ref_high } = parseReferenceRange(test.reference || '');

      const labResult = {
        patient_id: patientId,
        source_document_id: documentId,
        test_name: test.name,
        value: typeof test.value === 'number' ? test.value : parseFloat(test.value),
        unit: test.unit || null,
        ref_low,
        ref_high,
        collected_at: collectedDate
      };

      const { error } = await supabase
        .from('lab_results_onboarding')
        .insert(labResult);

      if (error) {
        console.error('[saveLabResults] Erro ao inserir:', error, labResult);
        errors++;
      } else {
        inserted++;
      }
    } catch (e) {
      console.error('[saveLabResults] Erro no teste:', e, test);
      errors++;
    }
  }

  console.log('[saveLabResults] Concluído:', { inserted, errors });
  return { inserted, errors };
}
